import { useEffect, useState, useCallback } from "react";
import { HandHeart, Loader2, Search, BookOpen, CheckCheck, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { brl, formatDateTime } from "@/lib/format";

const ORACAO_STYLE = {
  novo: "bg-amber-100 text-amber-800 border-amber-200",
  lido: "bg-blue-100 text-blue-800 border-blue-200",
  atendido: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const ORACAO_LABEL = { novo: "NOVO", lido: "LIDO", atendido: "ATENDIDO" };

export default function PrayerPanel() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = useCallback(async (term) => {
    try {
      const res = await api.get("/admin/oracoes", { params: term ? { q: term } : {} });
      setItems(res.data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const setOracaoStatus = async (c, status) => {
    setActing(c.id);
    try {
      await api.patch(`/admin/contribuicoes/${c.id}/oracao`, { status });
      toast.success(`Pedido marcado como ${ORACAO_LABEL[status]}.`);
      await load(q);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(null);
    }
  };

  return (
    <div>
      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          data-testid="oracao-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome ou telefone..."
          className="pl-12 h-12 text-base rounded-xl bg-white"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-800" />
        </div>
      ) : items.length === 0 ? (
        <div data-testid="oracoes-empty" className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500 text-lg">
          {q ? "Nenhum pedido encontrado para esta busca." : "Nenhum pedido de oração recebido ainda."}
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((c) => {
            const st = c.oracao_status || "novo";
            return (
              <div key={c.id} data-testid={`oracao-row-${c.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                      <HandHeart className="w-5 h-5 text-blue-800" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-heading text-lg font-bold text-slate-900">{c.nome}</p>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${ORACAO_STYLE[st]}`}>
                          {ORACAO_LABEL[st]}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        {c.whatsapp && <span className="font-semibold text-slate-700">{c.whatsapp} · </span>}
                        Meta: <span className="font-semibold text-slate-700">{c.meta_referente}</span> · {brl(c.valor)} · {formatDateTime(c.created_at)}
                      </p>
                      <p className="mt-2 text-base text-slate-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                        {c.pedido_oracao}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0 lg:flex-col lg:items-stretch">
                    {st !== "lido" && (
                      <button
                        data-testid={`oracao-lido-${c.id}`}
                        onClick={() => setOracaoStatus(c, "lido")}
                        disabled={acting === c.id}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-800 hover:bg-blue-700 text-white font-bold text-sm transition-colors disabled:opacity-60"
                      >
                        {acting === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
                        Marcar como lido
                      </button>
                    )}
                    {st !== "atendido" && (
                      <button
                        data-testid={`oracao-atendido-${c.id}`}
                        onClick={() => setOracaoStatus(c, "atendido")}
                        disabled={acting === c.id}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors disabled:opacity-60"
                      >
                        <CheckCheck className="w-4 h-4" /> Marcar como atendido
                      </button>
                    )}
                    {st !== "novo" && (
                      <button
                        data-testid={`oracao-novo-${c.id}`}
                        onClick={() => setOracaoStatus(c, "novo")}
                        disabled={acting === c.id}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm border border-slate-200 transition-colors disabled:opacity-60"
                      >
                        <RotateCcw className="w-4 h-4" /> Voltar para novo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
